import React, { useState, useRef, useEffect } from 'react';
import {
  Target,
  DollarSign,
  School,
  Users,
  FileText,
  Calendar,
  Sparkles,
  RefreshCw,
  ChevronDown
} from 'lucide-react';

// Import tab components (to be created)
import CompetitivePositionTab from './CompetitivePositionTab';
import FinancialAnalysisTab from './FinancialAnalysisTab';
import AcademicsTab from './AcademicsTab';
import CampusCultureTab from './CampusCultureTab';
import EssaysTab from './EssaysTab';
import TimelineTab from './TimelineTab';

const StrategicOverview = ({
  colleges = [],
  currentUser,
  portfolioLocked = false,
  studentProfile = {},
  onDataRefresh,
  currentView = 'strategic',
  onViewChange,
  portfolioStatus = {},
  onDeleteCollege
}) => {
  // Persist active tab in localStorage so it survives page refreshes
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem('comparisonHub_activeTab');
    return saved || 'competitive';
  });
  const tabContentRef = useRef(null);
  const navRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);

  // Save active tab to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('comparisonHub_activeTab', activeTab);
  }, [activeTab]);

  // Detect when nav becomes sticky
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      { threshold: [1], rootMargin: '-128px 0px 0px 0px' } // top-32 = 128px
    );

    if (navRef.current) {
      observer.observe(navRef.current);
    }

    return () => {
      if (navRef.current) {
        observer.unobserve(navRef.current);
      }
    };
  }, []);

  // Handle tab change with scroll to tab content
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    // Scroll to the tab content section with offset for sticky nav
    if (tabContentRef.current) {
      const element = tabContentRef.current;
      const offsetTop = element.offsetTop - 200; // Account for sticky nav height + some padding
      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }
  };



  // Tab definitions
  const tabs = [
    {
      id: 'competitive',
      label: 'Competitive Position',
      icon: Target,
      description: 'Your strategic position at each school'
    },
    {
      id: 'financial',
      label: 'Financial',
      icon: DollarSign,
      description: 'Costs, aid, and financial positioning'
    },
    {
      id: 'academics',
      label: 'Academics',
      icon: School,
      description: 'Academic programs and fit'
    },
    {
      id: 'culture',
      label: 'Campus Culture',
      icon: Users,
      description: 'Culture, community, and social fit'
    },
    {
      id: 'essays',
      label: 'Essays',
      icon: FileText,
      description: 'Essay requirements and strategy'
    },
    {
      id: 'timeline',
      label: 'Timeline',
      icon: Calendar,
      description: 'Deadlines and application timeline'
    }
  ];

  if (colleges.length === 0) {
    return (
      <div className="text-center py-12">
        <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">
          Select colleges above to begin comparing
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Sentinel for sticky detection */}
      <div ref={navRef} className="h-0" />

      {/* Sticky Tab Navigation - Always visible at very top */}
      <div className={`sticky top-32 z-50 -mx-8 px-8 py-4 transition-colors duration-200 ${
        isSticky ? 'bg-slate-900' : ''
      }`}>
        <div className="bg-white border border-slate-200 rounded-xl p-1 flex gap-1 overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`px-4 py-2 border-0 text-sm font-medium cursor-pointer rounded-lg transition-all duration-200 whitespace-nowrap flex-shrink-0 flex items-center gap-2 ${
                  isActive
                    ? 'bg-slate-900 text-white'
                    : 'bg-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div ref={tabContentRef} className="mt-4">
        {activeTab === 'competitive' && (
          <CompetitivePositionTab
            colleges={colleges}
            currentUser={currentUser}
            portfolioLocked={portfolioLocked}
            studentProfile={studentProfile}
            onDataRefresh={onDataRefresh}
            onDeleteCollege={onDeleteCollege}
          />
        )}

        {activeTab === 'financial' && (
          <FinancialAnalysisTab
            colleges={colleges}
            currentUser={currentUser}
            portfolioLocked={portfolioLocked}
            studentProfile={studentProfile}
            onDataGenerated={onDataRefresh}
            onDeleteCollege={onDeleteCollege}
          />
        )}

        {activeTab === 'academics' && (
          <AcademicsTab
            colleges={colleges}
            currentUser={currentUser}
            portfolioLocked={portfolioLocked}
            studentProfile={studentProfile}
            onDataRefresh={onDataRefresh}
            onDeleteCollege={onDeleteCollege}
          />
        )}

        {activeTab === 'culture' && (
          <CampusCultureTab
            colleges={colleges}
            currentUser={currentUser}
            portfolioLocked={portfolioLocked}
            studentProfile={studentProfile}
            onDataGenerated={onDataRefresh}
            onDeleteCollege={onDeleteCollege}
          />
        )}

        {activeTab === 'essays' && (
          <EssaysTab
            colleges={colleges}
            currentUser={currentUser}
            portfolioLocked={portfolioLocked}
            studentProfile={studentProfile}
            onDataGenerated={onDataRefresh}
            onDeleteCollege={onDeleteCollege}
          />
        )}

        {activeTab === 'timeline' && (
          <TimelineTab
            colleges={colleges}
            currentUser={currentUser}
            studentProfile={studentProfile}
            onDataGenerated={onDataRefresh}
            onDeleteCollege={onDeleteCollege}
          />
        )}
      </div>

      {/* Portfolio Locked Indicator */}
      {portfolioLocked && (
        <div className="mt-6 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-green-600" />
          <p className="text-sm text-green-800">
            <span className="font-medium">Portfolio Locked:</span> Strategic annotations and AI insights are now available across all tabs
          </p>
        </div>
      )}
    </div>
  );
};

export default StrategicOverview;